import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  FileText, Download, Calendar as CalendarIcon, GraduationCap, 
  FileCheck, FolderOpen, Award, User
} from 'lucide-react';
import { toast } from 'sonner';
import { useWebSocket, DATA_REFRESH_EVENT } from '../../contexts/WebSocketContext';

const DocumentsPage = ({ onMenuClick }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const { isConnected } = useWebSocket();

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const params = activeTab !== 'all' ? { document_type: activeTab } : {};
      const response = await api.get('/documents', { params });
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Erreur lors du chargement des documents');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    const handleDataRefresh = (event) => {
      const { type } = event.detail;
      if (type === 'document_uploaded') {
        fetchDocuments();
        toast.success('Un nouveau document est disponible');
      }
    };

    window.addEventListener(DATA_REFRESH_EVENT, handleDataRefresh);
    return () => window.removeEventListener(DATA_REFRESH_EVENT, handleDataRefresh);
  }, [fetchDocuments]);

  const getDocumentIcon = (type) => {
    switch (type) {
      case 'pedagogique':
        return GraduationCap;
      case 'administratif':
        return FileCheck;
      case 'certificat':
        return Award;
      default:
        return FileText;
    }
  };

  const getDocumentColor = (type) => {
    switch (type) {
      case 'pedagogique':
        return 'text-violet-600 bg-violet-50 dark:bg-violet-900/20';
      case 'administratif':
        return 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20';
      case 'certificat':
        return 'text-amber-600 bg-amber-50 dark:bg-amber-900/20';
      default:
        return 'text-slate-600 bg-slate-50 dark:bg-slate-900/20';
    }
  };

  const getCategoryBadgeColor = (type) => {
    switch (type) {
      case 'pedagogique':
        return 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300';
      case 'administratif':
        return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
      case 'certificat':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  const handleDownload = async (doc) => {
    if (!doc?.id) {
      toast.info('Document non disponible en téléchargement');
      return;
    }
    try {
      const response = await api.get(`/documents/${doc.id}/download`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.original_filename || doc.title || `document_${doc.id}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Téléchargement de "${doc.title}"`);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error(
        error.response?.status === 403
          ? 'Accès non autorisé à ce document'
          : 'Erreur lors du téléchargement'
      );
    }
  };

  const renderEmptyState = () => {
    const messages = {
      all: 'Aucun document disponible pour le moment',
      pedagogique: 'Vos supports de cours et ressources pédagogiques apparaîtront ici',
      administratif: 'Vos documents administratifs (certificat de scolarité, convention, contrat...) apparaîtront ici',
      certificat: 'Vos certificats et attestations officielles apparaîtront ici'
    };

    return (
      <div className="text-center py-12">
        <FolderOpen className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <p className="text-slate-600 dark:text-slate-400 mb-2">Aucun document disponible</p>
        <p className="text-sm text-slate-500">{messages[activeTab]}</p>
      </div>
    );
  };

  const renderDocumentList = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
        </div>
      );
    }

    const filteredDocs = activeTab === 'all' 
      ? documents 
      : documents.filter(d => d.document_type === activeTab);

    if (filteredDocs.length === 0) {
      return renderEmptyState();
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredDocs.map((doc) => {
          const Icon = getDocumentIcon(doc.document_type);
          return (
            <Card 
              key={doc.id} 
              className="hover:shadow-lg transition-all group border-l-4"
              style={{ 
                borderLeftColor: doc.document_type === 'pedagogique' ? '#8b5cf6' 
                  : doc.document_type === 'administratif' ? '#10b981' 
                  : '#f59e0b' 
              }}
              data-testid={`document-card-${doc.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-12 h-12 rounded-xl ${getDocumentColor(doc.document_type)} flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-1 line-clamp-2">
                      {doc.title}
                    </h4>
                    {doc.category_label && (
                      <Badge className={`text-xs ${getCategoryBadgeColor(doc.document_type)}`}>
                        {doc.category_label}
                      </Badge>
                    )}
                  </div>
                </div>
                
                {doc.description && (
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-3 line-clamp-2">
                    {doc.description}
                  </p>
                )}
                
                <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400 mb-4">
                  <div className="flex items-center gap-1">
                    <CalendarIcon className="w-3 h-3" />
                    {new Date(doc.generated_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </div>
                  {doc.uploaded_by && (
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {doc.uploaded_by}
                    </div>
                  )}
                </div>
                
                <Button 
                  variant="outline" 
                  className="w-full group-hover:bg-violet-50 dark:group-hover:bg-violet-900/20" 
                  onClick={() => handleDownload(doc)}
                  data-testid={`download-document-btn-${doc.id}`}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  const getTabStats = (type) => {
    if (type === 'all') return documents.length;
    return documents.filter(d => d.document_type === type).length;
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Mes Documents" onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-2">Mes Documents</h2>
          <p className="text-slate-600 dark:text-slate-400">
            Retrouvez les documents mis à disposition par votre centre de formation
          </p>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('all')}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                <FileText className="w-5 h-5 text-slate-600 dark:text-slate-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{documents.length}</p>
                <p className="text-xs text-slate-500">Tous</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('administratif')}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{getTabStats('administratif')}</p>
                <p className="text-xs text-slate-500">Administratifs</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('pedagogique')}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/20 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{getTabStats('pedagogique')}</p>
                <p className="text-xs text-slate-500">Pédagogiques</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('certificat')}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center">
                <Award className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{getTabStats('certificat')}</p>
                <p className="text-xs text-slate-500">Certificats</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 grid w-full max-w-2xl grid-cols-4">
            <TabsTrigger value="all" data-testid="tab-all" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Tous</span>
            </TabsTrigger>
            <TabsTrigger value="administratif" data-testid="tab-administratif" className="flex items-center gap-2">
              <FileCheck className="w-4 h-4" />
              <span className="hidden sm:inline">Administratifs</span>
            </TabsTrigger>
            <TabsTrigger value="pedagogique" data-testid="tab-pedagogique" className="flex items-center gap-2">
              <GraduationCap className="w-4 h-4" />
              <span className="hidden sm:inline">Pédagogiques</span>
            </TabsTrigger>
            <TabsTrigger value="certificat" data-testid="tab-certificat" className="flex items-center gap-2">
              <Award className="w-4 h-4" />
              <span className="hidden sm:inline">Certificats</span>
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value={activeTab}>
            <Card>
              <CardContent className="p-6">
                {renderDocumentList()}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default DocumentsPage;
